import { createInstance } from 'i18next';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getSentryHub } from '../utils/sentryHub.js';
import FetchBackend from './backend.js';
import { LOCALES } from './locales.js';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
let i18nInstance;
const useI18n = (realm, locale) => {
    const prevRealm = usePrevious(realm);
    const prevLocale = usePrevious(locale);
    if (prevRealm !== realm || prevLocale !== locale) {
        i18nInstance = createInstance({
            debug: process.env.NODE_ENV === 'development',
            lng: normalizeLocale(locale),
            ns: [realm],
            defaultNS: realm,
            fallbackLng: 'zu',
            supportedLngs: [...LOCALES.filter((l) => l.realm === realm).map((l) => l.locale), 'zu'],
            load: 'currentOnly',
            interpolation: {
                escapeValue: false, // not needed for react as it escapes by default
            },
            saveMissing: process.env.NODE_ENV === 'production',
            missingKeyHandler: () => '',
            parseMissingKeyHandler: (key) => (process.env.NODE_ENV === 'production' ? '' : key),
            backend: {
                loadPath: (language, namespace) => getTranslationsPath(namespace, language),
            },
            partialBundledLanguages: true,
        });
    }
    // do not chain use/init with createInstance!!!
    // we need the instance to be created so that the useTranslation
    // hook does not fail. The instance does not need to be ready,
    // but it needs to be there.
    useEffect(() => {
        i18nInstance.use(FetchBackend).init();
        i18nInstance.on('failedLoading', function (language, namespace, message) {
            getSentryHub().configureScope((scope) => {
                scope.setTags({
                    locale: language,
                    realm: namespace,
                });
            });
            getSentryHub().captureException(`Error loading translations: ${message}`);
        });
        i18nInstance.on('missingKey', function (language, namespace, key) {
            getSentryHub().configureScope((scope) => {
                scope.setTags({
                    locale: language[0],
                    realm: namespace,
                });
            });
            getSentryHub().captureMessage(`Missing translation key: ${key}`);
        });
        return () => {
            i18nInstance.off('failedLoading');
        };
    }, [realm, locale]);
};
const useTranslations = () => useTranslation(undefined, { i18n: i18nInstance });
// transform en_GB in en-GB (and make sure that country code is uppercase)
// return 'nb-NO' for 'no' and 'no-NO'
function normalizeLocale(locale) {
    const [lang, country] = locale.replace('_', '-').split('-');
    if (lang === 'no') {
        return 'nb-NO';
    }
    if (country) {
        return `${lang}-${country.toUpperCase()}`;
    }
    return lang;
}
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
function getTranslationsPath(namespace, locale) {
    if (window.location.host.includes('storybook-prod')) {
        return `https://cdn.public.zooplus.net/media/my-account-frame/locales/${namespace}/header-account-flyout/${locale}.json`;
    }
    if (window.location.host.includes('localhost') || window.location.host.includes('storybook')) {
        return `https://cdn-dev.public.zooplus.net/media/my-account-frame/locales/${namespace}/header-account-flyout/${locale}.json`;
    }
    return `https://cdn.public.zooplus.net/media/my-account-frame/locales/${namespace}/header-account-flyout/${locale}.json`;
}

export { useI18n, useTranslations };
//# sourceMappingURL=index.js.map
